import { useEffect, useState, useCallback } from 'react';
import { Box, Button, Modal, Icon, Tooltip, TextField, withStyles, InterestAccountTransferRequest, Label } from 'vopay';
import styles from './InterestAccountFundTransferModal.module.scss';
import usePostInterestAccountTransfer from '../../../hooks/useApi/usePostInterestAccountTransfer';
import InterestAccountFundTransferModalError from '../InterestAccountFundTransferModalError/InterestAccountFundTransferModalError';
import { Title } from 'UI';

type AddInterestAccountProps = {
  visible: boolean;
  onClose: (added: boolean) => void;
  transferType: 'Fund' | 'Withdraw';
  currentBalance: string;
  currency: string;
  formatAmount: (amount: string) => string;
};

const classes = withStyles(styles);

const InterestAccountFundTransferModal: React.FC<AddInterestAccountProps> = ({
  visible,
  onClose,
  transferType,
  currentBalance,
  currency,
  formatAmount,
}) => {
  const [fundAmount, setFundAmount] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [hasErrors, setHasErrors] = useState(false);
  const [responseErrorMessage, setResponseErrorMessage] = useState('');
  const [updatedBalance, setUpdatedBalance] = useState(currentBalance);

  const { postInterestAccountTransfer } = usePostInterestAccountTransfer();

  const transferTypeMap = {
    Fund: {
      title: 'Add Funds',
      accountType: 'Short Term',
      label: 'Add Funds',
      buttonText: 'Add Funds',
      tooltip:
        'The amount you put here will be transferred from your short term interest account to the long term interest account.',
    },
    Withdraw: {
      title: 'Withdraw',
      accountType: 'Long Term',
      label: 'Withdraw Amount',
      buttonText: 'Withdraw',
      tooltip: 'The amount will be transferred to the Short Term Interest Account.',
    },
  };

  const { title, accountType, label, buttonText, tooltip } = transferTypeMap[transferType];

  async function createTransaction() {
    const InterestAccountTransferParams: InterestAccountTransferRequest = {
      PaymentType: transferType,
      Amount: fundAmount.replace(/,/g, ''),
    };

    const postInterestAccountTransferResponse = await postInterestAccountTransfer(InterestAccountTransferParams);

    if (!postInterestAccountTransferResponse.Success) {
      setErrorMessage('Account creation failed for the following reason');
      setHasErrors(true);
      setResponseErrorMessage(postInterestAccountTransferResponse.ErrorMessage);
    } else {
      setFundAmount('');
      onClose(true);
    }
  }

  useEffect(() => {
    setUpdatedBalance(currentBalance);
  }, [currentBalance]);

  const updateBalance = useCallback(
    (amount: string) => {
      if (amount === '') {
        setUpdatedBalance(currentBalance);
      } else if (!Number.isNaN(parseFloat(amount))) {
        if (typeof amount === 'string') {
          const newBalance = parseFloat(currentBalance) - parseFloat(amount.replace(/,/g, ''));
          setUpdatedBalance(newBalance.toFixed(2));
        }
      }
    },
    [currentBalance],
  );

  const handleFundAmountChange = (amount: string) => {
    setFundAmount(amount);
    updateBalance(amount);
  };

  return (
    <>
      <Modal isVisible={visible} enableBgClick={false}>
        <div className={styles.interestAccountFundTransferModalContainer}>
          <Box height="auto" mb="md" base="8">
            <Title level="3">{title}</Title>

            <Box className={classes('addFundsCloseButton')}>
              <Icon
                onClick={() => {
                  setFundAmount('');
                  onClose(false);
                }}
                color="none"
                bgColor="none"
                icon="close2"
                size="sm"
              />
            </Box>
          </Box>

          <Box flx="col" gap="common" height="auto">
            <div className={classes('addFundsCard')}>
              Available {accountType} Balance
              <b>
                {currency} {formatAmount(updatedBalance)}
              </b>
            </div>

            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  marginBottom: '8px',
                }}
              >
                <Label htmlFor="interestAccountFundAmount" label={label} required isBold={false} padding="0" />

                <Tooltip
                  borderColor="rgba(45, 45, 45, 0.6)"
                  icon="info"
                  position="top"
                  bgColor="#fbfbfb"
                  information={tooltip}
                  popoverClassName={classes('tooltip')}
                />
              </div>

              <TextField
                id="interestAccountFundAmount"
                variant="outlineShadow"
                inputWidth="100%"
                containerWidth="100%"
                preset="currency"
                key="AddInterestAccountTransferAmount"
                name="AddInterestAccountTransferAmount"
                error={errorMessage}
                placeholder="Enter Amount"
                value={fundAmount}
                onBlur={(e) => {
                  validateAmount(e.target.value);
                  handleFundAmountChange(e.target.value);
                }}
                onChange={(e) => {
                  setFundAmount(e.target.value);
                }}
              />
            </div>

            <hr />

            <Button
              id="create-interest-account-transfer-button"
              label={buttonText}
              color="primary"
              type="button"
              isDisabled={errorMessage !== '' || parseFloat(fundAmount) === 0 || fundAmount === ''}
              onClick={() => {
                createTransaction();
              }}
            />
          </Box>
        </div>
      </Modal>

      <InterestAccountFundTransferModalError
        failureReason="Transaction failed for the following reason"
        errorMessage={responseErrorMessage ?? ''}
        visible={hasErrors}
        onClose={() => setHasErrors(false)}
      />
    </>
  );

  // TODO: use Yup for validation
  function validateAmount(val: string) {
    if (val === '') {
      setErrorMessage('');
      return;
    }

    if (!/^\d{1,3}(,\d{3})*(\.\d+)?$/.test(val) || parseFloat(val.replace(/,/g, '')) < 0) {
      setErrorMessage('Invalid number. Please try again.');
    } else if (parseFloat(val) > parseFloat(currentBalance)) {
      setErrorMessage('Amount cannot exceed the available short term balance.');
    } else {
      setErrorMessage('');
    }
  }
};

export default InterestAccountFundTransferModal;
